/**
 * 共用工具
 *
 * 還原自 chunk-common.js 模組 3204。原始模組共匯出 7 個函式，
 * webpack 匯出代號與對應如下（由 r.d(t,"x",...) 宣告讀出）：
 *
 *   e -> setStorage        b -> getStorage       a -> uuid
 *   c -> lockScroll        f -> unlockScroll     g -> sleep
 *   d -> isOutsideDeliveryArea
 */

import DELIVERY_AREA from './delivery-area.json'

/* ── 帶有效期的 localStorage ───────────────────────────────
 * 注意：雖然呼叫端變數常取名 cookie，實際用的是 localStorage，
 * 自行在值裡塞 expiry 時間戳模擬有效期。唯一真正的 cookie 是語言設定。
 */

export const setStorage = (key, value, seconds) => {
  const now = new Date()
  const item = { value, expiry: now.getTime() + 1000 * seconds }
  localStorage.setItem(key, JSON.stringify(item))
}

export const getStorage = (key) => {
  const raw = localStorage.getItem(key)
  if (!raw) return null

  const item = JSON.parse(raw)
  const now = new Date()

  if (now.getTime() > item.expiry) {
    localStorage.removeItem(key)
    return null
  }

  return item.value
}

/** 購物車項目 id */
export const uuid = () =>
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  )

/* ── 捲動鎖定（開啟對話框時用）─────────────────────────── */

export const lockScroll = () => {
  document.getElementsByClassName('page-body')[0].style.overflow = 'hidden'
}

export const unlockScroll = () => {
  document.getElementsByClassName('page-body')[0].style.overflow = 'auto'
}

/** 延遲 ms 毫秒 */
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/* ── 外送範圍判定 ─────────────────────────────────────────
 * DELIVERY_AREA 是 77 個頂點的多邊形，涵蓋青埔一帶
 * （經度 121.1968~121.2258、緯度 24.9996~25.0277）。
 *
 * 用 winding number 演算法：把多邊形各頂點對目標點的張角累加，
 * 總和約 ±2π 代表點在內部，約 0 代表在外部。
 *
 * ⚠️ 回傳值語意：**在範圍外時回傳 true**（角度總和為 0）。
 *    點正好落在邊上時回傳 false（視為在範圍內）。
 *    命名依實際行為而定，與原始碼的回傳邏輯一致。
 */
export const isOutsideDeliveryArea = (point) => windingNumberOutside(DELIVERY_AREA, point)

const windingNumberOutside = (polygon, point) => {
  const { lat, lng } = point
  let angleSum = 0

  for (let i = 0, n = polygon.length, prev = n - 1; i < n; prev = i, i++) {
    const curLat = polygon[i].lat
    const curLng = polygon[i].lng
    const prevLat = polygon[prev].lat
    const prevLng = polygon[prev].lng

    // 點落在這條邊上 -> 視為在範圍內
    if (
      (curLat - lat) * (lat - prevLat) >= 0 &&
      (curLng - lng) * (lng - prevLng) >= 0 &&
      (lat - curLat) * (prevLng - curLng) === (lng - curLng) * (prevLat - curLat)
    ) {
      return false
    }

    let angle = Math.atan2(curLng - lng, curLat - lat) - Math.atan2(prevLng - lng, prevLat - lat)

    if (angle >= Math.PI) angle -= 2 * Math.PI
    else if (angle <= -Math.PI) angle += 2 * Math.PI

    angleSum += angle
  }

  return Math.round(angleSum / Math.PI) === 0
}

/* ── textarea 自動調高 ────────────────────────────────────
 * 還原自 index.js 的 K() 與 B()（模組層級的區域函式，不在任何匯出模組中，
 * 因此不會出現在 import 對應表裡，需另外還原）。
 */

/** input 事件處理：先歸零再依內容高度撐開，+14 是原始碼的行高補償 */
function autoResizeHandler() {
  this.style.height = 'auto'
  this.style.height = `${this.scrollHeight + 14}px`
}

/** 對符合 selector 的 textarea 套用自動調高 */
export const autoResize = (selector) => {
  document.querySelectorAll(selector).forEach((el) => {
    el.style.height = `${el.scrollHeight}px`
    el.addEventListener('input', autoResizeHandler)
  })
}
